import * as React from 'react';

import * as TabsPrimitive from '@radix-ui/react-tabs';

import { cn } from '@/new/lib/utils';

type TabsVariant = 'default' | 'pill';

const TabsVariantContext = React.createContext<TabsVariant>('default');

const Tabs = TabsPrimitive.Root;

const listVariantClasses: Record<TabsVariant, string> = {
  default: 'inline-flex h-10.5 items-center justify-center border-b text-gray-500',
  pill: '-mt-0.5 inline-flex h-auto items-center overflow-clip rounded-[10px] border-0 bg-gray-100 p-0.5',
};

const triggerVariantClasses: Record<TabsVariant, string> = {
  default:
    'ring-offset-background focus-visible:ring-ring data-[state=active]:border-primary mb-[-2px] inline-flex items-center justify-center gap-2 border-b-2 border-transparent px-3 py-2.5 text-sm whitespace-nowrap transition-all focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=active]:border-teal-500 data-[state=active]:text-gray-900',
  pill: 'inline-flex items-center justify-center mb-0 gap-1.5 rounded-[8px] px-2.75 pt-1.25 pb-1 text-xs text-gray-500 whitespace-nowrap transition-colors hover:text-gray-600 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=active]:border-transparent data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-lg/3',
};

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & { variant?: TabsVariant }
>(({ className, variant = 'default', ...props }, ref) => (
  <TabsVariantContext.Provider value={variant}>
    <TabsPrimitive.List ref={ref} className={cn(listVariantClasses[variant], className)} {...props} />
  </TabsVariantContext.Provider>
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
  const variant = React.useContext(TabsVariantContext);
  return <TabsPrimitive.Trigger ref={ref} className={cn(triggerVariantClasses[variant], className)} {...props} />;
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'ring-offset-background focus-visible:ring-ring mt-2 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
